import { useEffect, useRef, useState } from 'react';
import styles from '@/components/ProbePage/ProbePage.module.css';
import { CameraIcon, EntryIcon, StartIcon } from '@/icons';
import { useSendCommand, useMachineStore } from '@/store/store';
import {
  CAMERA_IMAGE_URL,
  fetchMeasurement,
  type Measurement,
} from '@/components/ProbePage/probeData';
import defaultCameraImage from '@/assets/manuel-camera.webp';

type MeasureMode = 'camera' | 'manual';

// Manual-entry dimensions. Width and Length are required (measurement can't
// start without them); Height is optional.
const DIMENSIONS = [
  { key: 'width', label: 'Width', required: true },
  { key: 'length', label: 'Length', required: true },
  { key: 'height', label: 'Height', required: false },
] as const;

type DimensionKey = (typeof DIMENSIONS)[number]['key'];

// Dimensions are non-negative decimals capped at MAX_DIM. Both the input filter
// and the pre-send gate rely on this so an out-of-range or non-numeric value can
// never reach the M520 MDI string.
const MAX_DIM = 200;
const NUMERIC_INPUT = /^\d*\.?\d*$/;
const isValidDim = (value: string): boolean => {
  if (value.trim() === '') return false;
  const n = Number(value);
  return Number.isFinite(n) && n <= MAX_DIM;
};

export function ProbePage(): JSX.Element {
  const sendCommand = useSendCommand();
  const [measureMode, setMeasureMode] = useState<MeasureMode>('manual');
  const [dims, setDims] = useState<Record<DimensionKey, string>>({
    width: '',
    length: '',
    height: '',
  });

  // Camera flow state: an in-flight M521, the resulting capture URL, the read
  // I/J/K values, and any error surfaced to the operator.
  const [measuring, setMeasuring] = useState(false);
  const [cameraImage, setCameraImage] = useState<string | null>(null);
  const [cameraDims, setCameraDims] = useState<Measurement | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Start is enabled only in manual mode once both required dims are valid numbers.
  const canStart = measureMode === 'manual' && isValidDim(dims.width) && isValidDim(dims.length);

  // Push the entered dimensions to the machine: "M520 I<width> J<length> K<height>".
  // Height is optional, so its K word is only appended when a value is present.
  const handleStart = (): void => {
    if (!canStart) return;
    let command = `M520 I${dims.width.trim()} J${dims.length.trim()}`;
    if (isValidDim(dims.height)) command += ` K${dims.height.trim()}`;
    sendCommand({ type: 'MDI', command });
  };

  // Camera measurement completion detection. M521 is a remap M-code that blocks
  // the LinuxCNC interpreter until the vision python returns, so the mdiBusy
  // busy→idle transition is the authoritative "image + JSON written" signal.
  // `sawBusyRef` guards against a not-yet-registered op reading as idle;
  // `measuringRef` is read inside the subscribe callback (which closes over stale
  // state otherwise) and is flipped false the instant we start handling
  // completion so the follow-up M520's own busy cycle can't re-trigger us.
  const sawBusyRef = useRef(false);
  const measuringRef = useRef(false);
  const fallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFallback = (): void => {
    if (fallbackRef.current) {
      clearTimeout(fallbackRef.current);
      fallbackRef.current = null;
    }
  };

  const handleMeasureComplete = async (): Promise<void> => {
    measuringRef.current = false;
    sawBusyRef.current = false;
    clearFallback();
    try {
      // The M-code blocks until the JSON is written, but allow a couple of short
      // retries in case the file flush lands a beat after interp goes idle.
      let m = await fetchMeasurement();
      for (let i = 0; i < 3 && m === null; i++) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        m = await fetchMeasurement();
      }
      if (m === null) {
        // Vision backend isn't wired yet: M521 completes but produces no
        // measurement JSON. Until it's ready, fall back to the static capture
        // image instead of erroring. The rest of the flow below stays in place
        // for when the vision output starts landing.
        setCameraImage(`${CAMERA_IMAGE_URL}?t=${Date.now().toString()}`);
        setMeasuring(false);
        return;
      }
      // The measurement JSON is an external boundary; guard the numeric words so a
      // malformed I/J/K can never be injected into the M520 MDI string.
      if (!Number.isFinite(m.I) || !Number.isFinite(m.J)) {
        setCameraError('Invalid measurement values (I/J not numeric).');
        setMeasuring(false);
        return;
      }
      setCameraDims(m);
      setCameraImage(`${CAMERA_IMAGE_URL}?t=${Date.now().toString()}`);
      // Auto-send the measured dimensions. Height (K) is optional, so its word is
      // only appended when the vision output actually provided it.
      let command = `M520 I${m.I.toString()} J${m.J.toString()}`;
      if (m.K !== undefined && Number.isFinite(m.K)) command += ` K${m.K.toString()}`;
      sendCommand({ type: 'MDI', command });
    } catch {
      setCameraError('Could not retrieve measurement result.');
    } finally {
      setMeasuring(false);
    }
  };

  // Keep a live reference to the latest completion handler so the once-registered
  // subscribe callback never calls a stale closure (updated after every render).
  const onCompleteRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    onCompleteRef.current = () => {
      void handleMeasureComplete();
    };
  });

  useEffect(() => {
    return useMachineStore.subscribe((state, prev) => {
      if (!measuringRef.current) return;
      if (state.mdiBusy && !prev.mdiBusy) {
        sawBusyRef.current = true;
        clearFallback(); // real op running — drop the "never registered busy" net
        return;
      }
      if (!state.mdiBusy && prev.mdiBusy && sawBusyRef.current) {
        onCompleteRef.current();
      }
    });
  }, []);

  useEffect(() => () => clearFallback(), []);

  const handleCameraStart = (): void => {
    // M521 currently maps to save_wp_pos (placeholder): it completes instantly,
    // doesn't block the interpreter, and writes no measurement JSON yet — so
    // there's no busy→idle window to wait on. For now just fire it and show the
    // static capture image. When M521 itself is wired to the real vision — it
    // will block the interp while vision runs — restore the busy→idle wait: set
    // measuringRef, arm the fallback timeout (was MEASURE_TIMEOUT_MS = 60000 ms),
    // and let the subscribe effect drive handleMeasureComplete (kept below).
    setCameraError(null);
    sendCommand({ type: 'MDI', command: 'M521' });
    setCameraImage(`${CAMERA_IMAGE_URL}?t=${Date.now().toString()}`);
  };

  const isCamera = measureMode === 'camera';

  const startButton = (
    <button
      type="button"
      className={styles.startBtn}
      disabled={isCamera ? measuring : !canStart}
      onClick={isCamera ? handleCameraStart : handleStart}
    >
      <StartIcon className={styles.startIcon} />
      {isCamera ? (measuring ? 'Measuring…' : 'Start Camera') : 'Start Measurement'}
    </button>
  );

  return (
    <div className={styles.page}>
      <section className={styles.panel}>
        <div className={styles.measureToggle}>
          <button
            type="button"
            className={`${styles.measureTab} ${isCamera ? styles.measureTabActive : ''}`}
            onClick={() => setMeasureMode('camera')}
          >
            <CameraIcon className={styles.measureIcon} />
            Camera Measurement
          </button>
          <button
            type="button"
            className={`${styles.measureTab} ${!isCamera ? styles.measureTabActive : ''}`}
            onClick={() => setMeasureMode('manual')}
          >
            <EntryIcon className={styles.measureIcon} />
            Manuel Offset
          </button>
        </div>

        <div className={styles.cameraSection}>
          {isCamera ? (
            <p className={styles.cameraHint}>
              Position the probe in view, then start the camera measurement.
            </p>
          ) : (
            <div className={styles.sizeInputs}>
              {DIMENSIONS.map(({ key, label, required }) => (
                <label key={key} className={styles.sizeField}>
                  <span className={styles.sizeLabel}>
                    {label} (mm){required && ' *'}
                  </span>
                  <input
                    className={styles.sizeInput}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={dims[key]}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (NUMERIC_INPUT.test(value) && (value === '' || Number(value) <= MAX_DIM)) {
                        setDims((prev) => ({ ...prev, [key]: value }));
                      }
                    }}
                  />
                </label>
              ))}
            </div>
          )}
          <div className={styles.cameraImageBox}>
            <div className={styles.cameraViewport}>
              {measuring ? (
                <span className={styles.cameraPlaceholder}>Measuring…</span>
              ) : cameraError ? (
                <div className={styles.cameraInactive}>
                  <img src={defaultCameraImage} alt="" className={styles.cameraDefault} />
                  <h3 className={styles.cameraInactiveTitle}>Measurement Failed</h3>
                  <p className={styles.cameraInactiveText}>{cameraError}</p>
                  <span className={`${styles.cameraBadge} ${styles.cameraBadgeError}`}>
                    Camera error
                  </span>
                </div>
              ) : cameraImage ? (
                <img src={cameraImage} alt="Camera measurement" className={styles.cameraImage} />
              ) : (
                <div className={styles.cameraInactive}>
                  <img src={defaultCameraImage} alt="" className={styles.cameraDefault} />
                  <h3 className={styles.cameraInactiveTitle}>Camera Inactive</h3>
                  <p className={styles.cameraInactiveText}>
                    Turn on the camera to preview the workspace.
                  </p>
                  <span className={styles.cameraBadge}>Camera detected</span>
                </div>
              )}
            </div>
          </div>
          {isCamera && cameraDims && (
            <div className={styles.cameraReadback}>
              <span>Width: {cameraDims.I}</span>
              <span>Length: {cameraDims.J}</span>
              {cameraDims.K !== undefined && <span>Height: {cameraDims.K}</span>}
            </div>
          )}
        </div>

        {startButton}
      </section>
    </div>
  );
}
